import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs, Form, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Shared chart components
import StatsHourlyChart from '../../../statistics/charts/StatsHourlyChart';
import StatsDailyChart from '../../../statistics/charts/StatsDailyChart';
import StatsPerformanceCards from '../../../statistics/charts/StatsPerformanceCards';
import StatsCurrentRate from '../../../statistics/charts/StatsCurrentRate';
import StatsStatusBreakdown from '../../../statistics/charts/StatsStatusBreakdown';
import StatsAnomaliesDisplay from '../../../statistics/charts/StatsAnomaliesDisplay';

// New chart components
import StatsThroughputChart from '../../../statistics/charts/StatsThroughputChart';
import StatsPercentilesCards from '../../../statistics/charts/StatsPercentilesCards';
import StatsBatchesAnalysis from '../../../statistics/charts/StatsBatchesAnalysis';
import StatsHealthChart from '../../../statistics/charts/StatsHealthChart';

// Actions
import {
	get_request_stats_hourly,
	get_request_stats_daily,
	get_request_stats_current,
	get_request_stats_performance,
	get_request_stats_anomalies,
	get_request_stats_route,
	get_request_stats_throughput,
	get_request_stats_percentiles,
	get_request_stats_batches,
	get_request_stats_health
} from '../../../../actions/requestStatsActions';

// Utils
import { getLocalEpoch } from '../../../../utils/dateConverter';

const RequestStatsOverview = ({ serviceName, instanceId }) => {
	const dispatch = useDispatch();

	const {
		request_stats_loading,
		request_stats_hourly,
		request_stats_daily,
		request_stats_current,
		request_stats_performance,
		request_stats_anomalies,
		request_stats_route,
		request_stats_throughput,
		request_stats_percentiles,
		request_stats_batches,
		request_stats_health
	} = useSelector(state => state.requestStats);

	// TS stats state
	const [hours, setHours] = useState(24);
	const [days, setDays] = useState(7);
	const [route, setRoute] = useState('');
	const [routeHours, setRouteHours] = useState(24);

	// Aggregation state
	const [advancedTab, setAdvancedTab] = useState('throughput');
	const [aggStart, setAggStart] = useState('');
	const [aggEnd, setAggEnd] = useState('');
	const [granularity, setGranularity] = useState('hour');
	const [batchWindow, setBatchWindow] = useState(30);

	useEffect(() => {
		dispatch(get_request_stats_performance(serviceName, instanceId));
		dispatch(get_request_stats_current(serviceName, instanceId));
	}, []);

	const handleHourlyChange = (selectedHours) => {
		setHours(selectedHours);
		dispatch(get_request_stats_hourly(serviceName, instanceId, selectedHours));
	};

	const handleDailyChange = (selectedDays) => {
		setDays(selectedDays);
		dispatch(get_request_stats_daily(serviceName, instanceId, selectedDays));
	};

	const handleRouteSearch = () => {
		if (route) {
			dispatch(get_request_stats_route(serviceName, instanceId, route, routeHours));
		}
	};

	const buildAggFilters = (extra = {}) => {
		const filters = {};
		if (serviceName) filters.service = serviceName;
		if (instanceId) filters.instance = instanceId;
		if (aggStart) filters.start = getLocalEpoch(aggStart);
		if (aggEnd) filters.end = getLocalEpoch(aggEnd);
		return { ...filters, ...extra };
	};

	const handleThroughputSearch = () => {
		dispatch(get_request_stats_throughput(buildAggFilters({ granularity })));
	};

	const handlePercentilesSearch = () => {
		dispatch(get_request_stats_percentiles(buildAggFilters()));
	};

	const handleBatchesSearch = () => {
		dispatch(get_request_stats_batches(buildAggFilters({ batch_window: batchWindow })));
	};

	const handleHealthSearch = () => {
		dispatch(get_request_stats_health(buildAggFilters({ granularity })));
	};

	// Map performance data fields for shared components
	const performanceData = request_stats_performance?.data;
	const mappedPerformance = performanceData ? {
		total_jobs: performanceData.total_requests,
		success_rate: performanceData.success_rate,
		error_rate: performanceData.error_rate,
		avg_process_time: performanceData.avg_process_time
	} : null;

	const mappedRate = performanceData ? {
		jobs_per_minute: performanceData.total_requests
			? request_stats_current?.data?.rate_per_minute
			: 0,
		jobs_last_minutes: request_stats_current?.data?.requests_last_minutes
	} : request_stats_current?.data ? {
		jobs_per_minute: request_stats_current.data.rate_per_minute,
		jobs_last_minutes: request_stats_current.data.requests_last_minutes
	} : null;

	return (
		<Tabs
			defaultActiveKey='overview'
			className='mt-3'
			justify
		>
			<Tab eventKey='overview' title='Overview'>
				<div className='mt-3'>
					<StatsCurrentRate rate={mappedRate} />
					<StatsPerformanceCards performance={mappedPerformance} />
					<StatsStatusBreakdown breakdown={performanceData?.status_breakdown} />
				</div>
			</Tab>

			<Tab
				eventKey='hourly'
				title='Hourly'
				onEnter={() => { if (!request_stats_hourly?.data?.length) dispatch(get_request_stats_hourly(serviceName, instanceId, hours)); }}
			>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Select value={hours} onChange={(e) => handleHourlyChange(Number(e.target.value))}>
								<option value={12}>Last 12 hours</option>
								<option value={24}>Last 24 hours</option>
								<option value={48}>Last 48 hours</option>
							</Form.Select>
						</Col>
					</Row>
					<StatsHourlyChart data={request_stats_hourly?.data} loading={request_stats_loading} />
				</div>
			</Tab>

			<Tab
				eventKey='daily'
				title='Daily'
				onEnter={() => { if (!request_stats_daily?.data?.length) dispatch(get_request_stats_daily(serviceName, instanceId, days)); }}
			>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Select value={days} onChange={(e) => handleDailyChange(Number(e.target.value))}>
								<option value={7}>Last 7 days</option>
								<option value={14}>Last 14 days</option>
								<option value={30}>Last 30 days</option>
							</Form.Select>
						</Col>
					</Row>
					<StatsDailyChart data={request_stats_daily?.data} loading={request_stats_loading} />
				</div>
			</Tab>

			<Tab eventKey='route' title='By Route'>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Control
								placeholder='Route path (e.g. /api/payloads/upload)'
								value={route}
								onChange={(e) => setRoute(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') handleRouteSearch(); }}
							/>
						</Col>
						<Col xs={3}>
							<Form.Select value={routeHours} onChange={(e) => setRouteHours(Number(e.target.value))}>
								<option value={12}>12 hours</option>
								<option value={24}>24 hours</option>
								<option value={48}>48 hours</option>
							</Form.Select>
						</Col>
						<Col xs={2}>
							<button className='btn submit-btn' onClick={handleRouteSearch} disabled={!route}>
								<i className='bi bi-search me-1'></i> Search
							</button>
						</Col>
					</Row>
					{request_stats_route?.data?.by_hour &&
						<StatsHourlyChart data={request_stats_route.data.by_hour} loading={request_stats_loading} />
					}
				</div>
			</Tab>

			<Tab
				eventKey='anomalies'
				title='Anomalies'
				onEnter={() => { if (!request_stats_anomalies?.data?.anomalies) dispatch(get_request_stats_anomalies(serviceName, instanceId)); }}
			>
				<div className='mt-3'>
					<StatsAnomaliesDisplay
						anomalies={request_stats_anomalies?.data?.anomalies}
						summary={request_stats_anomalies?.data?.summary}
					/>
				</div>
			</Tab>

			<Tab eventKey='advanced' title='Advanced'>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={3}>
							<Form.Label className='mb-1 small'>Start</Form.Label>
							<Form.Control
								type='datetime-local'
								value={aggStart}
								onChange={(e) => setAggStart(e.target.value)}
							/>
						</Col>
						<Col xs={3}>
							<Form.Label className='mb-1 small'>End</Form.Label>
							<Form.Control
								type='datetime-local'
								value={aggEnd}
								onChange={(e) => setAggEnd(e.target.value)}
							/>
						</Col>
						<Col xs={2}>
							<Form.Label className='mb-1 small'>Granularity</Form.Label>
							<Form.Select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
								<option value='minute'>Minute</option>
								<option value='hour'>Hour</option>
								<option value='day'>Day</option>
							</Form.Select>
						</Col>
						<Col xs={2}>
							<Form.Label className='mb-1 small'>Batch Window (s)</Form.Label>
							<Form.Control
								type='number'
								min={5}
								max={300}
								value={batchWindow}
								onChange={(e) => setBatchWindow(Number(e.target.value))}
							/>
						</Col>
					</Row>

					<Tabs
						activeKey={advancedTab}
						onSelect={(k) => setAdvancedTab(k)}
						className='mb-3'
					>
						<Tab eventKey='throughput' title='Throughput'>
							<div className='mt-2'>
								<button className='btn submit-btn mb-3' onClick={handleThroughputSearch}>
									<i className='bi bi-graph-up me-1'></i> Load Throughput
								</button>
								<StatsThroughputChart data={request_stats_throughput} loading={request_stats_loading} />
							</div>
						</Tab>

						<Tab eventKey='percentiles' title='Percentiles'>
							<div className='mt-2'>
								<button className='btn submit-btn mb-3' onClick={handlePercentilesSearch}>
									<i className='bi bi-speedometer me-1'></i> Load Percentiles
								</button>
								<StatsPercentilesCards data={request_stats_percentiles} />
							</div>
						</Tab>

						<Tab eventKey='health' title='Health'>
							<div className='mt-2'>
								<button className='btn submit-btn mb-3' onClick={handleHealthSearch}>
									<i className='bi bi-heart-pulse me-1'></i> Load Health
								</button>
								<StatsHealthChart data={request_stats_health} loading={request_stats_loading} />
							</div>
						</Tab>

						<Tab eventKey='batches' title='Batches'>
							<div className='mt-2'>
								<button className='btn submit-btn mb-3' onClick={handleBatchesSearch}>
									<i className='bi bi-collection me-1'></i> Load Batches
								</button>
								<StatsBatchesAnalysis data={request_stats_batches} />
							</div>
						</Tab>
					</Tabs>
				</div>
			</Tab>
		</Tabs>
	);
}

RequestStatsOverview.propTypes = {
	serviceName: PropTypes.string.isRequired,
	instanceId: PropTypes.string
}

export default RequestStatsOverview;
